import React, { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle, ArrowRight, Shield, MapPin, CheckCircle, AlertCircle, FileSignature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contracts as contractsApi } from '../services/api/client';

/**
 * Modal to display contract validation errors and guide the user
 * Shows when a scan cannot be launched due to missing or invalid contract
 * Now allows user to:
 * - Continue without contract (with warning)
 * - Select an existing valid contract
 * - Go to workspace to create a contract
 */
const ContractErrorModal = ({
  isOpen,
  onClose,
  errorType, // 'contract_required' | 'target_not_in_perimeter'
  message,
  workspaceId,
  target,
  onContinueWithoutContract, // Callback to continue scan without contract
  onSelectContract // Callback when a contract is selected
}) => {
  const navigate = useNavigate();
  const [activeContracts, setActiveContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // Load active contracts when modal opens
  useEffect(() => {
    if (isOpen && workspaceId) {
      loadActiveContracts();
    }
  }, [isOpen, workspaceId]);

  const loadActiveContracts = async () => {
    if (!workspaceId) return;
    setLoadingContracts(true);
    try {
      const result = await contractsApi.getActiveForWorkspace(workspaceId);
      setActiveContracts(result.contracts || []);
    } catch (error) {
      console.error('Error loading contracts:', error);
      setActiveContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  if (!isOpen) return null;

  const handleGoToWorkspace = () => {
    onClose();
    navigate(`/workspaces/${workspaceId}?tab=contracts`);
  };

  const handleContinueWithoutContract = () => {
    if (onContinueWithoutContract) {
      onContinueWithoutContract();
    }
    onClose();
  };

  const handleSelectContract = () => {
    if (selectedContractId && onSelectContract) {
      onSelectContract(selectedContractId);
    }
    onClose();
  };

  const isContractRequired = errorType === 'contract_required';

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-brand-darker border border-yellow-500/30 rounded-2xl max-w-2xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {isContractRequired ? 'Contrat recommande' : 'Cible hors perimetre'}
              </h3>
              <p className="text-xs text-gray-400">Choisissez comment proceder</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning message */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-200 text-sm">
                  {isContractRequired
                    ? "Aucun contrat signe valide pour ce workspace. Un contrat est recommande pour documenter l'autorisation de pentest."
                    : `La cible "${target}" n'est pas dans le perimetre des contrats signes.`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Option 1: Select existing contract */}
          {activeContracts.length > 0 && (
            <div className="bg-brand-black rounded-xl p-4 border border-gray-700">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-brand-green" />
                Selectionner un contrat existant
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {activeContracts.map((contract) => (
                  <button
                    key={contract.id}
                    onClick={() => setSelectedContractId(contract.id)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedContractId === contract.id
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white">{contract.title || contract.contract_number}</div>
                        <div className="text-xs text-gray-400">
                          {contract.contract_number} | Valide jusqu'au {new Date(contract.end_date).toLocaleDateString('fr-FR')}
                        </div>
                        {contract.perimeter_summary && contract.perimeter_summary.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Perimetre: {contract.perimeter_summary.slice(0, 3).join(', ')}{contract.perimeter_summary.length > 3 ? '...' : ''}
                          </div>
                        )}
                      </div>
                      {selectedContractId === contract.id && (
                        <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {selectedContractId && (
                <button
                  onClick={handleSelectContract}
                  className="w-full mt-3 px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Utiliser ce contrat et lancer le scan
                </button>
              )}
            </div>
          )}

          {/* Option 2: Continue without contract */}
          {onContinueWithoutContract && (
            <div className="bg-brand-black rounded-xl p-4 border border-orange-500/30">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Continuer sans contrat
              </h4>
              <p className="text-gray-400 text-sm mb-3">
                Vous pouvez lancer le scan sans contrat, mais cette action est deconseille car:
              </p>
              <ul className="text-xs text-gray-500 space-y-1 mb-4">
                <li>• Pas de preuve formelle d'autorisation</li>
                <li>• Pas de definition claire du perimetre</li>
                <li>• Risques juridiques potentiels</li>
              </ul>
              <button
                onClick={handleContinueWithoutContract}
                className="w-full px-4 py-2 bg-orange-500/20 text-orange-400 font-medium rounded-lg hover:bg-orange-500/30 transition-colors border border-orange-500/30"
              >
                Je comprends, continuer sans contrat
              </button>
            </div>
          )}

          {/* Option 3: Create new contract */}
          <div className="bg-brand-black rounded-xl p-4 border border-gray-700">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400" />
              Créer un nouveau contrat
            </h4>
            <p className="text-gray-400 text-sm mb-3">
              Accédez au workspace pour créer et faire signer un contrat d'autorisation de pentest.
            </p>
            <button
              onClick={handleGoToWorkspace}
              className="w-full px-4 py-2 bg-blue-500/20 text-blue-400 font-medium rounded-lg hover:bg-blue-500/30 transition-colors border border-blue-500/30 flex items-center justify-center gap-2"
            >
              Aller aux contrats du workspace
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-brand-black/50 border-t border-gray-800 sticky bottom-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractErrorModal;
